/**
 * The supplier onboarding statuses of PRD §5.11.
 *
 * In progress order, which is how a reader thinks about them — a picker
 * ordered alphabetically would put APPROVED first and NOT_STARTED last.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.9, PRD §5.11
 */
import type {
  LegalFinance,
  SaveLegalFinanceRequest,
  SupplierOnboardingStatus,
} from '@nexus/contracts';

export const ONBOARDING_STATUSES: readonly SupplierOnboardingStatus[] = [
  'NOT_STARTED',
  'IN_REVIEW',
  'APPROVED',
] as const;

/** The editable text fields, with the column length each accepts. */
export const TEXT_FIELDS = [
  { key: 'registeredLegalName', max: 300 },
  { key: 'vatTaxId', max: 60 },
  { key: 'companyRegistrationNumber', max: 60 },
  { key: 'registeredAddress', max: 500 },
  { key: 'countryOfIncorporation', max: 2 },
  { key: 'invoicingContact', max: 300 },
  { key: 'paymentTerms', max: 200 },
  { key: 'invoicingCurrency', max: 3 },
] as const;

export type TextFieldKey = (typeof TEXT_FIELDS)[number]['key'];

/** The draft a reader is editing — every value a string the inputs can hold. */
export type LegalFinanceDraft = Record<TextFieldKey, string> & {
  ndaSignedDate: string;
  masterAgreementDate: string;
  supplierOnboardingStatus: SupplierOnboardingStatus;
};

/** Turns a record into editable strings. */
export function toDraft(record: LegalFinance): LegalFinanceDraft {
  return {
    registeredLegalName: record.registeredLegalName ?? '',
    vatTaxId: record.vatTaxId ?? '',
    companyRegistrationNumber: record.companyRegistrationNumber ?? '',
    registeredAddress: record.registeredAddress ?? '',
    countryOfIncorporation: record.countryOfIncorporation ?? '',
    invoicingContact: record.invoicingContact ?? '',
    paymentTerms: record.paymentTerms ?? '',
    invoicingCurrency: record.invoicingCurrency ?? '',
    /* A date input wants `yyyy-mm-dd`; the API returns a full timestamp. */
    ndaSignedDate: record.ndaSignedDate?.slice(0, 10) ?? '',
    masterAgreementDate: record.masterAgreementDate?.slice(0, 10) ?? '',
    supplierOnboardingStatus: record.supplierOnboardingStatus,
  };
}

/**
 * Turns a draft back into a save payload.
 *
 * An emptied field becomes `null`, not `''`. The two are the same to a reader
 * and different to the API — `null` clears the column, while `''` would be
 * stored and then reported as missing anyway, which is the same outcome by a
 * more confusing route.
 *
 * Lives here rather than beside the form for two reasons: exporting a function
 * from a component file breaks fast refresh, and this conversion is where a
 * "save" quietly loses a field — so it is worth asserting without a form in
 * the way.
 */
export function draftToPayload(
  draft: LegalFinanceDraft,
  version: number,
): SaveLegalFinanceRequest {
  const text = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  };

  /* A date input gives `yyyy-mm-dd`; the API wants a timestamp. Midnight UTC
     rather than local, so the date a reader typed is the date stored — a
     local-midnight conversion shifts it by a day either side of UTC. */
  const date = (value: string): string | null =>
    value.length === 0 ? null : `${value}T00:00:00.000Z`;

  return {
    version,
    registeredLegalName: text(draft.registeredLegalName),
    vatTaxId: text(draft.vatTaxId),
    companyRegistrationNumber: text(draft.companyRegistrationNumber),
    registeredAddress: text(draft.registeredAddress),
    countryOfIncorporation: text(draft.countryOfIncorporation),
    invoicingContact: text(draft.invoicingContact),
    paymentTerms: text(draft.paymentTerms),
    invoicingCurrency: text(draft.invoicingCurrency),
    ndaSignedDate: date(draft.ndaSignedDate),
    masterAgreementDate: date(draft.masterAgreementDate),
    supplierOnboardingStatus: draft.supplierOnboardingStatus,
  };
}
